import type { ComponentType, ReactNode, SVGProps } from 'react';
import type { MutationState, QueryClient, QueryFilters } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { EmptyState } from './EmptyState';
import { Button } from './Button';
import { Notice } from './Badge';
import { Skeleton } from './Skeleton';

/**
 * Data-honesty law, made mechanical: a failed or offline query must NEVER render as
 * "no data". Every v2 page branches three ways — pending (skeleton), error/offline
 * (these states), true-empty (an EmptyState). Shared so the law can't drift per page.
 */
type Icon = ComponentType<SVGProps<SVGSVGElement> & { size?: number }>;

export function ErrorState({
  icon,
  what,
  onRetry,
}: {
  icon: Icon;
  /** Plain-English object name, e.g. "customers" → "Couldn't load customers". */
  what: string;
  onRetry: () => void;
}) {
  return (
    <EmptyState
      icon={icon}
      title={`Couldn't load ${what}`}
      description="Something went wrong talking to the server. Your data is safe — try again."
      action={<Button onClick={onRetry}>Try again</Button>}
    />
  );
}

/**
 * The server refused this sign-in. Nothing went wrong, and nothing will go differently on a
 * second attempt.
 *
 * Every 403 in the product used to land in `ErrorState` above — "Something went wrong…
 * try again", under a button that can only fail. That is a permanent authorization state
 * dressed as a transient failure, and the retry is the tell: it invites the one action
 * guaranteed not to work, so the user does it twice before believing the screen.
 *
 * Same distinction as `GoneState`, one status code along: a failed query is not "no data",
 * a missing record is not a failed query, and *not yours to open* is none of the three.
 * Branch at the CALL SITE the way the pages already do for `GoneState` — one law, one
 * idiom, and a reader of `<ErrorState/>` can still trust it to be an error state.
 *
 * ⚠️ The copy stays deliberately vague about WHY. `FORBIDDEN` is overloaded four ways in
 * `trpc.ts`: two are real permission refusals, and two are "no active organization" / "no
 * role in this organization" — a stale session, which a fresh sign-in fixes. Naming the
 * owner as the person who can grant access would be confident and wrong for half of them.
 */
export function ForbiddenState({ icon, what }: { icon: Icon; what: string }) {
  return (
    <EmptyState
      icon={icon}
      title={`You don't have access to ${what}`}
      description="This account can't open it. If that looks wrong, sign out and back in — and if it still says this, ask whoever set up the shop's Servwell."
    />
  );
}

/** Is this the server refusing the caller, rather than the server having a bad day? */
export function isForbidden(error: unknown): boolean {
  const code = (error as { data?: { code?: string } } | null)?.data?.code;
  return code === 'FORBIDDEN';
}

/**
 * The record is gone — deleted, or a link somebody kept. NOT an error: nothing went wrong
 * and there is nothing to retry, so offering "Try again" is a button that can only fail.
 * Same distinction the data-honesty law draws at the top of this file, one level down: a
 * failed query is not "no data", and a missing record is not a failed query.
 */
export function GoneState({
  icon,
  what,
  backTo,
  backLabel,
}: {
  icon: Icon;
  /** e.g. "message" → "That message is gone". */
  what: string;
  backTo: string;
  backLabel: string;
}) {
  return (
    <EmptyState
      icon={icon}
      title={`That ${what} is gone`}
      description={`It was deleted, or the link is older than the ${what}. Nothing is broken.`}
      action={
        <Link to={backTo}>
          <Button>{backLabel}</Button>
        </Link>
      }
    />
  );
}

/** Is this the server saying "no such thing", rather than the server having a bad day? */
export function isNotFound(error: unknown): boolean {
  const code = (error as { data?: { code?: string } } | null)?.data?.code;
  return code === 'NOT_FOUND';
}

/**
 * The query can't run because there's no connection and nothing cached.
 *
 * The fourth branch of the honesty law, and it had no importers: a paused query is
 * `isPending` forever, so every page rendered a skeleton that would never resolve. A
 * skeleton means "this is arriving"; with no signal and no cache, it isn't.
 *
 * Use `queryState` below rather than checking `isPaused` by hand — a page that forgets
 * is a page that shows the endless skeleton again, and nothing in the compiler notices.
 */
export function OfflineState({ icon, what }: { icon: Icon; what: string }) {
  return (
    <EmptyState
      icon={icon}
      title="No signal"
      description={`${what.charAt(0).toUpperCase() + what.slice(1)} hasn't been saved to this phone yet, so it'll load when you're back on a connection. Anything you've already opened today still works.`}
    />
  );
}

/**
 * Which of the four honest states a query is in, in the order they must be checked.
 *
 * `isPaused` HAS to come before `isPending`, because a paused query is also pending — the
 * same trap that left the tech's Finish button reading "Finishing…" forever. Returning a
 * union rather than booleans is what stops a page checking them in the wrong order.
 */
export type QueryPhase = 'offline' | 'loading' | 'error' | 'ready';

export function queryState(q: {
  isPaused: boolean;
  isPending: boolean;
  isError: boolean;
}): QueryPhase {
  if (q.isPaused && q.isPending) return 'offline';
  if (q.isPending) return 'loading';
  if (q.isError) return 'error';
  return 'ready';
}

/**
 * The phase a surface should RENDER, given it may already be holding the answer.
 *
 * ⭐ **An error is only a reason to blank a surface when there is nothing left to show.**
 *
 * `queryState` answers a question about the QUERY. This answers the question a surface
 * actually has, which is different: *can I draw?* query-core keeps `data` through a
 * background error, so a card that branches on the phase throws away correct, on-screen
 * content to announce that a REFRESH failed. That is reachable on any settings page left
 * open — `staleTime` is 30s and `refetchOnWindowFocus` defaults on, so tabbing away and back
 * over one flaky moment is enough.
 *
 * ⚠️ It does NOT mean "pretend nothing happened". A surface that keeps stale content has to
 * SAY so, and `refreshFailed` below is the other half of this pair — see the argument there,
 * which is a repro, not a preference.
 *
 * Only `'error'` can co-occur with data today: a paused or pending query holding a cached
 * answer is already `'success'` in query-core, so `queryState` calls it ready. Written
 * generally anyway, because the next state added to the union should not have to remember.
 */
export function shownState(
  q: { isPaused: boolean; isPending: boolean; isError: boolean },
  data: unknown,
): QueryPhase {
  const state = queryState(q);
  return state === 'ready' || data !== undefined ? 'ready' : state;
}

/**
 * The body below is real, and the last attempt to refresh it failed.
 *
 * 🔴 **Silence here can be a lie, and the Codex adversary produced the repro.** Disconnect an
 * AI connection: the mutation succeeds, `done()` invalidates, the refetch fails. query-core
 * keeps the pre-disconnect data, so the card goes on saying the connection is ACTIVE —
 * directly contradicting the thing the reader just did and watched succeed. Stale content is
 * usually merely old; once a write has landed against it, it is wrong.
 *
 * That settles the open question on ticket 042 in favour of saying it. The rest of the app
 * stays silent through a failed background refetch, which is TanStack's default and fine for
 * a read-only list; these are the surfaces you go to in order to CHANGE something, and one of
 * them can be made to state the opposite of what just happened.
 */
export function refreshFailed(
  q: { isPaused: boolean; isPending: boolean; isError: boolean },
  data: unknown,
): boolean {
  return q.isError && data !== undefined;
}

/**
 * A quiet line with a way out, over a surface that is still worth reading.
 *
 * NOT `ErrorState`: nothing is missing from the screen and "Couldn't load" would be false. A
 * `Notice` rather than a modal or a toast for the same reason — the failure is real and it is
 * not urgent, because what is below it is either still true or honestly blank.
 */
function RetryNotice({
  show,
  onRetry,
  children,
}: {
  show: boolean;
  onRetry: () => void;
  children: ReactNode;
}) {
  if (!show) return null;
  return (
    <Notice tone="warn">
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span>{children}</span>
        <button
          type="button"
          className="underline underline-offset-2 hover:no-underline"
          onClick={onRetry}
        >
          Try again
        </button>
      </span>
    </Notice>
  );
}

/**
 * Said once, above content that is real but older than it looks. Pair of `refreshFailed`.
 *
 * ⚠️ The copy is NEUTRAL about the write on purpose, and the first draft was not. It said
 * "What you changed was still saved" — reassurance that reads well after a successful save
 * and is a flat contradiction after a refused one, because `onError` invalidates too. If that
 * invalidating refetch also fails, the red refusal and a yellow "still saved" render together
 * on the same card. `refreshFailed` knows only that cached data exists; it does not know
 * whether a write landed, so it must not speak for one. (Codex adversary, round 3.)
 */
export function StaleNotice({ show, onRetry }: { show: boolean; onRetry: () => void }) {
  return (
    <RetryNotice show={show} onRetry={onRetry}>
      This didn&apos;t refresh just now, so what&apos;s below is from the last time it
      loaded.
    </RetryNotice>
  );
}

/**
 * A row of KPI tiles whose aggregate never arrived.
 *
 * ⚠️ `—` in a tile is already honest — it says "we don't know", never "zero" — and it was also
 * a dead end: nothing on the page could ask again, so the figures stayed blank until some
 * unrelated refetch happened to fix them. Honest and unrecoverable is still a screen a person
 * cannot get an answer out of.
 *
 * Beside the tiles rather than replacing them, because one failed aggregate is no reason to
 * take away a list that loaded perfectly well underneath it (042).
 */
export function FiguresNotice({
  show,
  what,
  onRetry,
}: {
  show: boolean;
  /** e.g. "quote totals" → "We couldn't work out your quote totals just now." */
  what: string;
  onRetry: () => void;
}) {
  return (
    <RetryNotice show={show} onRetry={onRetry}>
      We couldn&apos;t work out {what} just now, so those figures are blank rather than
      wrong.
    </RetryNotice>
  );
}

/**
 * The state of a card whose ONE body is made of SEVERAL queries.
 *
 * 🔴 This exists because of the exact bug it replaces. `AutomationsCard` needed two answers
 * and spelled the combination `if (q.isPending || !draft || !policy) return <Skeleton/>`.
 * Read that carefully: when either query ERRORS, `isPending` goes false and `data` stays
 * undefined, so the local draft is never set, so `!draft` is true — **forever**. A failed
 * query rendered as a skeleton that could never resolve, and the second query could blank
 * the whole card while the first one was perfectly fine.
 *
 * The order is the whole content of this function, and each step is a rule the repo learned
 * separately:
 *
 *  - **offline beats error.** With no signal, "Couldn't load — Try again" is a button that
 *    can only fail; that is the `ForbiddenState` lesson one status along. Say the true and
 *    actionable thing: there is no connection.
 *  - **error beats loading.** This is the infinite skeleton. If any query the body needs has
 *    failed, the body can NEVER be drawn — so a skeleton is a promise of arrival that
 *    nothing will keep. Say it failed.
 *  - **loading beats ready**, or a half-built body renders as a whole one.
 *
 * Pure and variadic so it can be table-tested: the property that matters is invisible from
 * outside a rendered card, and this repo has shipped guards that could not fail because the
 * thing they described was only observable through the component.
 */
export function worstState(...states: QueryPhase[]): QueryPhase {
  if (states.includes('offline')) return 'offline';
  if (states.includes('error')) return 'error';
  if (states.includes('loading')) return 'loading';
  return 'ready';
}

/**
 * The phase to render for a card that has no data to draw.
 *
 * A query reporting `ready` while holding no data cannot happen through tRPC — every
 * procedure returns something. This exists because the compiler does not know that, and the
 * alternative is the same three-word ternary repeated at a dozen call sites where one of
 * them will eventually be typed as `'loading'`.
 *
 * ⚠️ `'error'` is deliberate, and it is the SAFE direction. The failure this whole ticket
 * exists to remove is a card that renders a skeleton it can never resolve; falling back to
 * `'loading'` would rebuild exactly that, one impossible state along. An error at least says
 * something is wrong and offers the retry.
 */
export function unresolved(state: QueryPhase): Exclude<QueryPhase, 'ready'> {
  return state === 'ready' ? 'error' : state;
}

/**
 * A count is a CLAIM. Do not make one until the query has answered.
 *
 * `Checklists (0)` under a body that says "Loading…" is one card contradicting itself, and
 * the zero is the half a person believes — they read the heading and move on. The same
 * heading on a FAILED query tells a shop with forty checklists that it has none.
 *
 * `HourlyCostCard` got this right first and said why in a comment; this is that comment made
 * into the only way to write it. Every settings card routes its count through here for the
 * same reason `costFigure()` is the only way to format a cost on `PartsUsedCard`: fixing the
 * four call sites that exist today is exactly the shape that leaks on the fifth.
 *
 * ⚠️ `n` is evaluated by the caller either way, so it must be safe to compute on absent data
 * (`data?.length ?? 0` is fine — the point is that it is not SHOWN, not that it is not
 * computed). What the guard forbids is interpolating a count into a title without this.
 */
export function counted(title: string, state: QueryPhase, n: number): string {
  return state === 'ready' ? `${title} (${n})` : title;
}

/**
 * Which error a multi-query surface should answer for.
 *
 * ⚠️ `a.error ?? b.error` takes the FIRST error, not the one that decided the state. If
 * `orders` 500s and `vendors` 403s, `??` hands over the 500, `isForbidden` says no, and the
 * card offers a Try-again that refetches both — the 403 refuses again and the reader is stuck
 * behind a button that can never fully succeed. That is the "button that can only fail" the
 * forbidden dispatch exists to remove, so a refusal has to win.
 *
 * This is the cost of moving the dispatch inward (009's ruling, re-argued for `UnresolvedState`):
 * a call site knew WHICH query failed and the component gets one error. So the call site has
 * to say which one still matters, and this is that sentence, written once.
 */
export function worstError(...errors: unknown[]): unknown {
  return errors.find((e) => isForbidden(e)) ?? errors.find((e) => e != null);
}

/**
 * What ANY surface renders when the query behind it has not answered.
 *
 * ⚠️ Named `CardState` at first, and renamed because the name had stopped being true: about
 * half its call sites are not settings cards — a `DataTable`'s empty slot, three inventory
 * tabs, a job-detail sheet, and the whole Schedule page. A component whose name describes
 * one of its callers is the same defect this ticket is named after, one layer up.
 *
 * ⭐ **One contract for the family.** Fifteen cards each invented their own answer to "the
 * query didn't come back", and the result was five idioms: an infinite skeleton, a card that
 * deleted itself, an empty endpoint offered for copying, "Nothing extra asked here" over a
 * failure, and — in one card — the correct four-way branch. This is that fifth one, lifted
 * out. Nothing here is new; `HourlyCostCard` has rendered exactly this since ticket 009.
 *
 * ⚠️ **`state` cannot be `'ready'` ON PURPOSE.** The call site must branch, and the compiler
 * makes it: there is no way to hand this component a resolved query and have it quietly
 * render a spinner over live data.
 *
 * ⚠️ **Yes, this dispatches FORBIDDEN itself, and ticket 009 forbade exactly that — for
 * `ErrorState`.** The distinction is load-bearing rather than lawyerly. `ErrorState` is a
 * PRESENTATIONAL component whose name is a promise about what it is; hiding a 403 inside it
 * means a reader of `<ErrorState/>` can no longer trust the name, which is why 009 reverted
 * it and why the detail pages still branch at their call sites. `UnresolvedState` is a state
 * DISPATCHER whose entire job is choosing — it composes the untouched primitives and asserts
 * nothing about which one you get. The alternative is fifteen cards each remembering to ask
 * `isForbidden`, and the count of cards that remember today is one.
 */
export function UnresolvedState({
  state,
  what,
  icon,
  error,
  onRetry,
  skeleton = 'h-24',
}: {
  /** `'ready'` is unrepresentable — see the docblock. */
  state: Exclude<QueryPhase, 'ready'>;
  /**
   * A noun phrase that reads in all three sentences: "Couldn't load ___", "You don't have
   * access to ___", "___ will be here when you're back on a connection." e.g. "your price
   * book". Checked by the guard, because a `what` that only reads in one of them produces
   * correct-looking copy that is ungrammatical in the state nobody tested.
   */
  what: string;
  icon: Icon;
  /** The query's error, for the FORBIDDEN check. Absent is treated as an ordinary failure. */
  error?: unknown;
  onRetry: () => void;
  skeleton?: string;
}) {
  if (state === 'loading') return <Skeleton className={skeleton} />;
  if (state === 'offline') {
    // NOT `OfflineState`, whose copy says "hasn't been saved to this phone yet" — that is
    // the tech PWA talking, and /settings is a desk. `HourlyCostCard` made this call first.
    return (
      <Notice tone="warn">
        You&apos;re offline. {what.charAt(0).toUpperCase() + what.slice(1)} will be here
        when you&apos;re back on a connection.
      </Notice>
    );
  }
  return isForbidden(error) ? (
    <ForbiddenState icon={icon} what={what} />
  ) : (
    <ErrorState icon={icon} what={what} onRetry={onRetry} />
  );
}

/**
 * The same law for WRITES — and the distinction the whole offline surface turns on:
 * **queued is not sending, and sending is not delivered.**
 *
 * `queryState` above has existed since the tech pages were built; writes had no sibling, so
 * "on the wire" and "waiting for a network that isn't there" were one word. That is not
 * cosmetic — the two available spellings fail in opposite directions:
 *
 *  - `isPending` alone locks a control FOREVER offline, because a paused mutation never
 *    settles: one tap in a basement and the button is dead until signal returns.
 *  - `isPending && !isPaused` does the reverse — the control stays live with a write
 *    already queued behind it, so every further tap queues another one.
 *
 * Returning a union rather than booleans is what makes the wrong order unrepresentable, and
 * `delivered` is named for what the SERVER did, not for what the component did: "done" is
 * the word that let a banner announce work had gone through the moment the queue un-paused.
 *
 * **The parameter is `{isPaused, status}` on purpose.** That is the intersection of the two
 * shapes this has to serve: a `useMutation()` result, and a `Mutation['state']` straight off
 * `getMutationCache()`. The cache side carries no `isPending`/`isError`/`isSuccess` at all —
 * so typing this against the hook result would have forced the banner and the replay queue,
 * which read the cache, to hand-map booleans at each call site. That per-site remapping is
 * the exact drift this helper exists to end.
 */
export type MutationLike = {
  isPaused: boolean;
  status: 'idle' | 'pending' | 'error' | 'success';
};

/**
 * Compile-time proof that a mutation-CACHE entry really does satisfy `MutationLike`.
 *
 * It has to live here, in `src`, because `apps/web/tsconfig.json` only includes `src` —
 * the test files are transpiled by Vitest and never type-checked, so no runtime test can
 * make a claim about types. Without this, the docstring's promise that the banner and the
 * replay queue can pass their cache state straight in would be unverified prose, and would
 * break silently the first time TanStack reshaped `MutationState`.
 */
type Assert<T extends true> = T;
type _CacheStateIsMutationLike = Assert<
  MutationState extends MutationLike ? true : false
>;

/**
 * Whether a control must refuse a second tap.
 *
 * `mutationState` is a CLASSIFIER — it names where a write is, and deliberately does not
 * decide anything. This is the decision, and it exists because the two obvious spellings of
 * it are both wrong in ways that took a ticket each to find:
 *
 *  - `isPending` alone locks the control forever offline (a paused mutation never settles);
 *  - `sending` alone leaves it live with a write already queued behind it, so **every
 *    further tap queues another one** — and because each tap mints a fresh `mutationId`,
 *    the idempotency key that exists precisely to stop this cannot see them as the same
 *    intent. On the payment control that is N taps, N payments, against a customer standing
 *    there watching.
 *
 * `queued` and `sending` are both "asked for, not yet answered", which is the only thing a
 * control needs to know. Written once here rather than per site, because per-site is exactly
 * how one screen ended up with four buttons right and two wrong.
 */
export function awaitingServer(m: MutationLike): boolean {
  const phase = mutationState(m);
  return phase === 'queued' || phase === 'sending';
}

export function mutationState(
  m: MutationLike,
): 'queued' | 'sending' | 'failed' | 'delivered' | 'idle' {
  // `isPaused` first, and alone. Its sibling above needs `isPaused && isPending` because a
  // paused QUERY holding cached data is `ready` — it has an answer, it just can't refresh.
  // A write has no such state: unsent is unsent. (In query-core 5.x `isPaused` is only ever
  // set alongside `status: 'pending'` and is cleared on settle, so the two spellings agree
  // today; this one is the honest semantic rather than a fix for a reachable bug.)
  if (m.isPaused) return 'queued';
  if (m.status === 'pending') return 'sending';
  if (m.status === 'error') return 'failed';
  if (m.status === 'success') return 'delivered';
  return 'idle';
}

/**
 * A write's `onSuccess` that keeps the write pending until the read that SHOWS its outcome
 * (`shows`) is back, while every other read on screen is refreshed, as a screen's
 * fire-and-forget `refresh` does.
 *
 * 🔴 For a write whose control the screen draws from that read. Settling first left a moment
 * where the stop still read its old status with the control live (the write settles before a
 * fire-and-forget refresh lands), and a second tap there sent a second write with a fresh key,
 * which the server refuses into the changes panel. Pending, the control stays shut
 * (`awaitingServer`) until the new status is on screen.
 *
 * ⚠️ Only `shows` is waited on. Awaiting every read would hold the write on the slowest card
 * below the fold. The cost, accepted: a re-read that loses the signal midway holds the write
 * (and its control) until the signal is back, when the old status is all the phone has.
 *
 * ⚠️ tRPC runs the key-level `onSuccess` (offline-replay's `onLanded`) only when the screen's
 * returns nothing, so a write using this skips it. `onLanded` acts on notes alone.
 */
export function refreshUntilShown(qc: QueryClient, shows: QueryFilters): Promise<void> {
  const shown = qc.invalidateQueries(shows);
  // Then every other read, JOINING the one above rather than cancelling it and asking again.
  void qc.invalidateQueries(undefined, { cancelRefetch: false });
  return shown;
}

/**
 * A row as the screen drew it: its id and its version. `updated_at` is stamped by a trigger
 * on every UPDATE (`set_updated_at`), so two draws with the same key show the same facts, and
 * a write that lands changes it. What a tap lock is keyed to: a lock taken on one drawing
 * never matches another stop, or the same stop after it has moved.
 */
export function drawnAs(row: { id: string; updatedAt: string | Date }): string {
  return `${row.id}@${new Date(row.updatedAt).getTime()}`;
}
